import React, { useState, useEffect } from 'react';
import { createPortal } from 'react-dom';
import { useNavigate } from 'react-router-dom';
import { Icons } from '../components/Icons';
import {
  getAllAnnouncementsAdmin,
  createAnnouncement,
  updateAnnouncement,
  deleteAnnouncement,
  getAnnouncementImageUrl,
} from '../services/api';
import { AnnouncementsSkeleton } from '../components/SkeletonLoader';
import { useWebSocketEvent } from '../hooks/useWebSocket';

// --- Post Announcement Page ---
const PostAnnouncement = () => {
  const navigate = useNavigate();

  // Form state
  const [title, setTitle] = useState('');
  const [category, setCategory] = useState('General');
  const [message, setMessage] = useState('');
  const [posting, setPosting] = useState(false);
  const [formError, setFormError] = useState('');

  // Image state — a newly selected file, its preview URL, and (while editing)
  // the announcement's existing image so it can be shown/removed
  const [imageFile, setImageFile] = useState(null);
  const [imagePreview, setImagePreview] = useState('');
  const [existingImage, setExistingImage] = useState('');
  const [existingImageRemoved, setExistingImageRemoved] = useState(false);

  // Editing state — when set, the form is repurposed to edit this announcement
  const [editingId, setEditingId] = useState(null);

  // Real announcement list
  const [announcements, setAnnouncements] = useState([]);
  const [loading, setLoading] = useState(true);
  const [deletingId, setDeletingId] = useState(null);

  const tagColors = {
    General: 'bg-[#bbf7d0] text-green-800',
    Info: 'bg-blue-100 text-blue-700',
    Urgent: 'bg-red-100 text-red-700',
  };

  // Fetches all announcements (including inactive ones) for the management list
  const fetchAnnouncements = async () => {
    try {
      const res = await getAllAnnouncementsAdmin();
      setAnnouncements(res.data);
    } catch (error) {
      console.error('Failed to fetch announcements:', error);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchAnnouncements();
  }, []);

  // Real-time synchronization
  useWebSocketEvent("ANNOUNCEMENT_UPDATED", () => {
    fetchAnnouncements();
  });

  const handleClear = () => {
    if (imagePreview && imagePreview.startsWith('blob:')) {
      URL.revokeObjectURL(imagePreview);
    }
    setTitle('');
    setCategory('General');
    setMessage('');
    setImageFile(null);
    setImagePreview('');
    setExistingImage('');
    setExistingImageRemoved(false);
    setEditingId(null);
    setFormError('');
  };

  // Pre-fills the form with an existing announcement's data for editing
  const handleEditClick = (announcement) => {
    setEditingId(announcement.announcement_id);
    setTitle(announcement.title);
    setCategory(announcement.category || 'General');
    setMessage(announcement.content);
    setImageFile(null);
    setImagePreview('');
    setExistingImage(announcement.image_path || '');
    setExistingImageRemoved(false);
    setFormError('');
    window.scrollTo({ top: 0, behavior: 'smooth' });
  };

  // Validates and previews a newly selected image file
  const handleImageChange = (e) => {
    const file = e.target.files && e.target.files[0];
    if (!file) return;
    if (!['image/png', 'image/jpeg', 'image/gif', 'image/webp'].includes(file.type)) {
      setFormError('Please choose a valid image (PNG, JPG, GIF, or WEBP).');
      e.target.value = '';
      return;
    }
    if (file.size > 10 * 1024 * 1024) {
      setFormError('Image must be under 10MB.');
      e.target.value = '';
      return;
    }
    if (imagePreview && imagePreview.startsWith('blob:')) {
      URL.revokeObjectURL(imagePreview);
    }
    setImageFile(file);
    setImagePreview(URL.createObjectURL(file));
    setExistingImageRemoved(false);
    setFormError('');
    e.target.value = '';
  };

  // Removes the current image (a new selection, or the existing one while editing)
  const handleRemoveImage = () => {
    if (imageFile) {
      if (imagePreview && imagePreview.startsWith('blob:')) {
        URL.revokeObjectURL(imagePreview);
      }
      setImageFile(null);
      setImagePreview('');
    } else if (existingImage) {
      setImagePreview('');
      setExistingImageRemoved(true);
    }
  };

  // Creates a new announcement, or saves changes if currently editing one
  const handleSubmit = async (e) => {
    e.preventDefault();
    if (!title.trim() || !message.trim()) {
      setFormError('Title and message are both required.');
      return;
    }
    setFormError('');
    setPosting(true);
    try {
      const formData = new FormData();
      formData.append('title', title);
      formData.append('content', message);
      formData.append('category', category);
      if (imageFile) {
        formData.append('image', imageFile);
      } else if (editingId && existingImage && existingImageRemoved) {
        formData.append('remove_image', 'true');
      }

      if (editingId) {
        await updateAnnouncement(editingId, formData);
      } else {
        await createAnnouncement(formData);
      }
      await fetchAnnouncements();
      handleClear();
    } catch (error) {
      if (error.userMessage) {
        setFormError(error.userMessage);
      } else if (error.response && error.response.data && error.response.data.detail) {
        const detail = error.response.data.detail;
        if (typeof detail === 'string') {
          setFormError(detail);
        } else if (Array.isArray(detail)) {
          setFormError(detail.map((e) => e.msg || JSON.stringify(e)).join(', '));
        } else {
          setFormError(JSON.stringify(detail));
        }
      } else {
        setFormError(error.message || 'Failed to save the announcement. Please try again.');
      }
    } finally {
      setPosting(false);
    }
  };

  // Deletes an announcement permanently — this is the real admin delete,
  // distinct from the org-side "Archive" feature which only hides it per-org
  const handleConfirmDelete = async () => {
    try {
      await deleteAnnouncement(deletingId);
      await fetchAnnouncements();
      if (editingId === deletingId) handleClear();
    } catch (error) {
      console.error('Failed to delete announcement:', error);
      const detail = error.response?.data?.detail || error.message || 'Failed to delete the announcement. Please try again.';
      alert(typeof detail === 'string' ? detail : JSON.stringify(detail));
    } finally {
      setDeletingId(null);
    }
  };

  const announcementToDelete = announcements.find((a) => a.announcement_id === deletingId);

  return (
    <div className="max-w-4xl space-y-6">
      {/* Back Button */}
      <button
        onClick={() => navigate('/admin/dashboard')}
        className="flex items-center text-sm font-medium text-gray-500 hover:text-primary transition-colors group"
      >
        <div className="mr-1 group-hover:-translate-x-1 transition-transform">
          <Icons.ChevronDown className="rotate-90 w-4 h-4" />
        </div>
        Back to Dashboard
      </button>

      {/* New / Edit Announcement Form */}
      <div className="bg-white rounded-[20px] border border-gray-200 shadow-sm p-8">
        <h3 className="text-[17px] font-bold text-primary mb-6">
          {editingId ? 'Edit Announcement' : 'New Announcement'}
        </h3>

        {formError && (
          <div className="mb-5 bg-red-50 border border-red-200 rounded-lg px-4 py-3 text-red-600 text-sm font-medium">
            {formError}
          </div>
        )}

        <form onSubmit={handleSubmit} className="space-y-6">
          <div>
            <label className="block text-[13px] font-semibold text-gray-600 mb-2">Title</label>
            <input
              type="text"
              value={title}
              onChange={(e) => setTitle(e.target.value)}
              placeholder="Announcement title..."
              className="w-full border border-gray-300 rounded-lg px-4 py-3 focus:outline-none focus:ring-2 focus:ring-primary/20 focus:border-primary transition-all text-sm placeholder-gray-400 font-medium"
            />
          </div>

          <div>
            <label className="block text-[13px] font-semibold text-gray-600 mb-2">Tag</label>
            <div className="relative">
              <select
                value={category}
                onChange={(e) => setCategory(e.target.value)}
                className="w-full border border-gray-300 rounded-lg px-4 py-3 focus:outline-none focus:ring-2 focus:ring-primary/20 focus:border-primary transition-all appearance-none text-sm bg-white font-medium text-gray-700"
              >
                <option>General</option>
                <option>Info</option>
                <option>Urgent</option>
              </select>
              <div className="absolute inset-y-0 right-0 flex items-center pr-4 pointer-events-none text-gray-400">
                <Icons.ChevronDown />
              </div>
            </div>
          </div>

          <div>
            <label className="block text-[13px] font-semibold text-gray-600 mb-2">Message</label>
            <textarea
              value={message}
              onChange={(e) => setMessage(e.target.value)}
              placeholder="Write your announcement here..."
              rows="5"
              className="w-full border border-gray-300 rounded-lg px-4 py-3 focus:outline-none focus:ring-2 focus:ring-primary/20 focus:border-primary transition-all resize-none text-sm placeholder-gray-400 font-medium"
            ></textarea>
          </div>

          <div>
            <label className="block text-[13px] font-semibold text-gray-600 mb-2">Image (optional)</label>
            <label className="cursor-pointer flex items-center justify-center gap-2 w-full border-2 border-dashed border-gray-300 hover:border-[#086C53] hover:bg-[#f0f9f6] transition-all rounded-xl py-6 text-sm text-gray-500 font-medium">
              <Icons.Upload />
              {imageFile ? 'Change image' : 'Click to upload an image'}
              <input
                type="file"
                accept="image/png,image/jpeg,image/gif,image/webp"
                onChange={handleImageChange}
                className="hidden"
              />
            </label>

            {(imageFile || (existingImage && !existingImageRemoved)) && (
              <div className="mt-4 relative rounded-xl overflow-hidden border border-gray-200">
                {imagePreview ? (
                  <img
                    src={imagePreview}
                    alt="Preview"
                    className="w-full h-auto max-h-[70vh] object-contain bg-gray-50"
                  />
                ) : (
                  <img
                    src={getAnnouncementImageUrl({
                      announcement_id: editingId,
                      image_path: existingImage,
                    })}
                    alt="Current announcement"
                    className="w-full h-auto max-h-[70vh] object-contain bg-gray-50"
                  />
                )}
                <button
                  type="button"
                  onClick={handleRemoveImage}
                  className="absolute top-3 right-3 p-2 rounded-lg bg-black/60 text-white hover:bg-red-500 transition-colors"
                  title="Remove image"
                >
                  <Icons.Trash className="w-4 h-4" />
                </button>
              </div>
            )}
          </div>

          <div className="flex justify-end space-x-3 pt-2">
            <button
              type="button"
              onClick={handleClear}
              disabled={posting}
              className="px-6 py-2.5 border border-gray-300 text-gray-600 font-semibold rounded-lg hover:bg-gray-50 transition-colors text-sm disabled:opacity-60"
            >
              {editingId ? 'Cancel' : 'Clear'}
            </button>
            <button
              type="submit"
              disabled={posting}
              className="px-8 py-2.5 bg-sidebar text-white font-semibold rounded-lg hover:bg-primary transition-colors text-sm shadow-sm disabled:opacity-60"
            >
              {posting ? 'Saving...' : editingId ? 'Save Changes' : 'Post'}
            </button>
          </div>
        </form>
      </div>

      {/* Manage Announcements Section */}
      <div className="bg-white rounded-[20px] border border-gray-200 shadow-sm overflow-hidden">
        <div className="bg-[#086C53] px-7 py-4">
          <h3 className="text-white font-bold text-[17px]">Manage Announcements</h3>
        </div>

        {loading ? (
          <div className="p-6">
            <AnnouncementsSkeleton count={3} />
          </div>
        ) : announcements.length === 0 ? (
          <div className="p-10 text-center text-gray-400 text-sm">No announcements posted yet.</div>
        ) : (
          <div className="divide-y divide-gray-100">
            {announcements.map((announcement) => {
              const postedDate = announcement.created_at
                ? new Date(announcement.created_at).toLocaleDateString('en-US', { month: 'long', day: 'numeric', year: 'numeric' })
                : '';
              const tagClass = tagColors[announcement.category] || 'bg-gray-100 text-gray-600';
              return (
                <div key={announcement.announcement_id} className="p-7 hover:bg-gray-50/50 transition-colors flex justify-between items-start group">
                  <div className="space-y-3">
                    <span className={`${tagClass} text-[10px] font-bold px-3 py-1 rounded-full uppercase tracking-wider`}>
                      {announcement.category || 'General'}
                    </span>
                    <div>
                      <h4 className="text-[15px] font-semibold text-gray-800 flex items-center gap-2">
                        {announcement.title}
                        {announcement.image_path && (
                          <span className="flex items-center gap-1 text-[10px] font-bold text-[#086C53] bg-[#e0f5ee] px-2 py-0.5 rounded-full uppercase tracking-wider">
                            <Icons.Upload className="w-3 h-3" />
                            Image
                          </span>
                        )}
                      </h4>
                      <p className="text-xs text-gray-400 mt-1 font-medium">
                        Posted {postedDate}
                        {!announcement.is_active && <span className="ml-2 text-gray-400 italic">(inactive)</span>}
                      </p>
                    </div>
                  </div>
                  <div className="flex space-x-2">
                    <button
                      onClick={() => handleEditClick(announcement)}
                      className="p-1.5 text-gray-400 hover:text-gray-600 border border-gray-200 rounded hover:bg-gray-100 transition-colors"
                    >
                      <Icons.Edit />
                    </button>
                    <button
                      onClick={() => setDeletingId(announcement.announcement_id)}
                      className="p-1.5 text-red-400 hover:text-red-600 border border-gray-200 rounded hover:bg-red-50 transition-colors"
                    >
                      <Icons.Trash />
                    </button>
                  </div>
                </div>
              );
            })}
          </div>
        )}
      </div>

      {/* Delete Confirmation Modal rendered via portal to cover entire screen */}
      {deletingId !== null &&
        createPortal(
          <div
            className="fixed inset-0 z-[9999] flex items-center justify-center p-4 bg-black/50 backdrop-blur-sm"
            onClick={() => setDeletingId(null)}
          >
            <div
              className="bg-white rounded-[20px] shadow-2xl border border-gray-100 p-8 w-full max-w-sm"
              onClick={(e) => e.stopPropagation()}
            >
              <div className="w-16 h-16 bg-red-100 rounded-full flex items-center justify-center mx-auto mb-4 text-red-600">
                <Icons.Trash />
              </div>
              <h3 className="text-[18px] font-bold text-gray-900 text-center mb-1">Delete this announcement?</h3>
              <p className="text-sm text-gray-500 text-center mb-8">
                {announcementToDelete ? `"${announcementToDelete.title}" will be permanently removed for everyone.` : ''}
              </p>
              <div className="flex space-x-3">
                <button
                  type="button"
                  onClick={() => setDeletingId(null)}
                  className="flex-1 py-2.5 rounded-xl border border-gray-200 text-sm font-bold text-gray-600 hover:bg-gray-50 transition-colors"
                >
                  Cancel
                </button>
                <button
                  type="button"
                  onClick={handleConfirmDelete}
                  className="flex-1 py-2.5 rounded-xl bg-red-500 hover:bg-red-600 text-white text-sm font-bold transition-colors shadow-sm"
                >
                  Yes, Delete
                </button>
              </div>
            </div>
          </div>,
          document.body
        )}
    </div>
  );
};

export default PostAnnouncement;